import React from "react";
import { Paper, InputLabel, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { BriefcaseAccountOutline, CloseCircle } from "mdi-material-ui";
import { ProjectFragment } from "@graphql";
import { ThemeType } from "@theme";
import locale from "@bonliva-crm/locale";
import { Body } from "@components";
import { UserAvatarChip } from "./UserAvatarChip";

type Props = {
  data: ProjectFragment;
  costCenter: string;
  withClear?: boolean;
  clearIcon?: React.ReactNode;
  onClear?: () => void;
};

export function ProjectCard(props: Props) {
  const { clearIcon = <CloseCircle />, data, costCenter } = props;

  const classes = useStyles({});

  const ownerAdminMerged =
    data.Owner && data.Admin && data.Owner.PersonId === data.Admin.PersonId;

  const ownerLabel = ownerAdminMerged ? `${locale.Owner}/${locale.Admin}` : locale.Owner;

  const members = data.Owner
    ? data.Members.filter((x) => x.PersonId !== data.Owner.PersonId)
    : data.Members;

  return (
    <Paper className={classes.root}>
      <div className={classes.header} style={{ marginBottom: 6 }}>
        <BriefcaseAccountOutline fontSize="small" />
        <Body className={classes.projectNumber} style={{ marginTop: 2 }}>
          {locale.Project}: {data.Name} ({data.ProjectNumber})
        </Body>
        {costCenter && (
          <Body style={{ marginTop: 2 }}>
            {locale.CostCenter}: {costCenter}
          </Body>
        )}
      </div>

      <div className={classes.participants}>
        {data.Owner && (
          <div className={classes.item}>
            <InputLabel shrink>{ownerLabel}</InputLabel>
            <UserAvatarChip size="small" userName={data.Owner.Name} />
          </div>
        )}

        {!ownerAdminMerged && data.Admin && (
          <div className={classes.item}>
            <InputLabel shrink>{locale.Admin}</InputLabel>
            <UserAvatarChip size="small" userName={data.Admin.Name} />
          </div>
        )}

        {members.map((x) => (
          <div key={x.PersonId} className={classes.item}>
            <InputLabel shrink>{locale.Member}</InputLabel>
            <UserAvatarChip size="small" userName={x.Name} />
          </div>
        ))}
      </div>

      {data.Description && (
        <div className={classes.item1}>
          <InputLabel shrink>{locale.Description}</InputLabel>
          <Body>{data.Description}</Body>
        </div>
      )}

      {data.Comment && (
        <div className={classes.item1}>
          <InputLabel shrink>{locale.Comment}</InputLabel>
          <Body>{data.Comment}</Body>
        </div>
      )}

      {props.withClear && (
        <div className={classes.rightWrapper}>
          <IconButton onClick={props.onClear}>{clearIcon}</IconButton>
        </div>
      )}
    </Paper>
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    ...theme.mixins.childrenMarginBottom(8),
    padding: 16,
    position: "relative"
  },

  header: {
    ...theme.mixins.childrenMarginRight(4),
    display: "flex"
  },

  projectNumber: {
    fontWeight: "bold"
  },

  participants: {
    ...theme.mixins.childrenMarginRight(4),
    display: "flex",
    flexWrap: "wrap"
  },

  item: {
    display: "inline-flex",
    flexDirection: "column"
  },

  item1: {
    display: "flex",
    flexDirection: "column"
  },

  rightWrapper: {
    position: "absolute",
    right: 0,
    bottom: 0,
    top: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16
  }
}));
